import { useMemo } from 'react';
import { useCurrency, useIntl, useOrderIncrements, useSecurity } from '../../hooks';
import {
  ExecutionReport,
  OrdTypeEnum,
  Order,
  PricingModeEnum,
  SecurityTypesUtils,
  SideEnum,
  UnifiedLiquidityEnum,
  type Security,
} from '../../types';
import { getSymbolsAcrossMarkets, isCounterCurrency, toBig } from '../../utils';
import { getPricingReference } from '../../utils/pricingMode';
import { Box, HStack } from '../Core';
import { InlineFormattedNumber, SmartTruncateEnum } from '../FormattedNumber';
import { Icon, IconName } from '../Icons';
import { QuoteSide, Side } from '../Side';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import { UnifiedLiquidityTooltip } from '../UnifiedLiquidity';
import { MarketPart } from './MarketPart';
import { StrategyPart } from './StrategyPart';
import { Wrapper } from './styles';

import { get } from 'lodash-es';
import { defineMessages } from 'react-intl';
import { useStrategiesContext } from '../../contexts';
import { DDHOrderIcon } from '../DDH/DDHOrderIcon';
import { Currency } from '../FormattedNumber/styles';
import type { SmartSummaryProps } from './types';

export type { SmartSummaryProps } from './types';

const messages = defineMessages({
  buy: {
    defaultMessage: 'Buy',
    id: 'SmartSummary.buy',
  },
  sell: {
    defaultMessage: 'Sell',
    id: 'SmartSummary.sell',
  },
  ofCurrency: {
    defaultMessage: 'of {currency}',
    id: 'SmartSummary.ofCurrency',
  },
  withCurrency: {
    defaultMessage: 'with {currency}',
    id: 'SmartSummary.withCurrency',
  },
  forCurrency: {
    defaultMessage: 'for {currency}',
    id: 'SmartSummary.forCurrency',
  },
  quoteSummary: {
    defaultMessage: '<base></base> {Side, select, Buy {with} other {for}} <quote></quote>',
    id: 'SmartSummary.quoteSummary',
  },
  unit: {
    defaultMessage: 'Unit',
    id: 'SmartSummary.unit',
  },
  units: {
    defaultMessage: 'Units',
    id: 'SmartSummary.units',
  },
});

export function SmartSummary({
  entity,
  type,
  showSymbol = false,
  showPrice = true,
  showQty = true,
  showQtyCurrency = false,
  showSide = true,
  qtyField = 'OrderQty',
  priceField = 'Price',
  showLastMarket = false,
  showStrategy = false,
  displayStrategy,
  showPriceReference = false,
  smartTruncate = SmartTruncateEnum.None,
  showFixingDetails = false,
  ...props
}: SmartSummaryProps) {
  const { formatMessage } = useIntl();

  const getSideLabel = (side: SideEnum) => {
    if (side === SideEnum.Buy) {
      return formatMessage(messages.buy);
    } else if (side === SideEnum.Sell) {
      return formatMessage(messages.sell);
    }
    return side;
  };

  const security = useSecurity(entity.Symbol);
  const currency = useCurrency(entity.Currency);
  const { strategiesByName } = useStrategiesContext();
  const strategy = strategiesByName.get(entity.Strategy || '');
  const orderIncrements = useOrderIncrements(entity);

  const quantity: string | undefined = entity[qtyField];
  let price: string | undefined = entity[priceField];
  const isCcy = security != null && isCounterCurrency(entity.Currency, security);
  const underlyingSymbols = getSymbolsAcrossMarkets({
    Markets: entity.Markets,
    LegSummary: entity instanceof Order ? entity.LegSummary : undefined,
  });
  const showUnderlyingSymbols = entity.unifiedLiquidity === UnifiedLiquidityEnum.Enabled;
  const lastMarket = entity.Markets?.length === 1 ? entity.Markets?.at(0)?.Market : entity.LastMarket;
  const lastMarketEntry = lastMarket ? entity.Markets?.find(m => m.Market === lastMarket) : undefined;
  const displaySide = lastMarketEntry?.Side ?? entity.Side;
  const isCompoundSecurity = SecurityTypesUtils.Compound.isProductType(security);
  const quantityCurrency = isCompoundSecurity
    ? formatMessage(toBig(quantity)?.eq(1) ? messages.unit : messages.units)
    : currency?.Symbol;
  const quantityIncrement = orderIncrements.quantityIncrement;

  if (type === 'order') {
    const showFees = entity.OrdType === OrdTypeEnum.LimitAllIn;
    if (priceField === 'AvgPx') {
      price = showFees ? entity.AvgPxAllIn : entity.AvgPx;
    }
    let endPrice: string | undefined = undefined;
    // Only show end price when compared to entity.Price not other fields like AvgPx
    if (
      (entity instanceof Order || entity instanceof ExecutionReport) &&
      priceField === 'Price' &&
      strategy?.showPriceAsRange &&
      strategy.endPriceParamName
    ) {
      endPrice = get(entity.Parameters, strategy.endPriceParamName);
    }

    return (
      <Wrapper {...props}>
        {showSymbol && (
          <Tooltip
            disabled={!showUnderlyingSymbols || underlyingSymbols.length === 0}
            tooltip={<UnifiedLiquidityTooltip security={security} symbols={underlyingSymbols} />}
          >
            <HStack gap="spacing4">
              <Text color="colorTextImportant" data-testid="order-symbol">
                <span>{security?.DisplaySymbol ?? security?.Symbol ?? entity.Symbol}</span>
                {showUnderlyingSymbols ? '+' : ''}
              </Text>
              {entity instanceof Order && <DDHOrderIcon order={entity} />}
            </HStack>
          </Tooltip>
        )}
        {showSide && displaySide && (
          <Side side={displaySide} data-testid="order-side">
            {getSideLabel(displaySide)}
          </Side>
        )}
        {showQty && (
          <InlineFormattedNumber
            highlightAll
            number={quantity}
            increment={quantityIncrement}
            specification={security?.SizeDisplaySpec}
            currency={quantityCurrency}
            smartTruncate={smartTruncate}
          />
        )}
        {showQty && showQtyCurrency && security && (
          <Currency>
            {(() => {
              switch (true) {
                case entity.Side === SideEnum.Buy && isCcy:
                case entity.Side === SideEnum.Sell && isCcy:
                  return formatMessage(messages.ofCurrency, { currency: entity.AmountCurrency });
                case entity.Side === SideEnum.Buy && !isCcy:
                  return formatMessage(messages.withCurrency, { currency: entity.AmountCurrency });
                case entity.Side === SideEnum.Sell && !isCcy:
                  return formatMessage(messages.forCurrency, { currency: entity.AmountCurrency });
                default:
                  return null;
              }
            })()}
          </Currency>
        )}
        {showQty && showPrice && price && <Icon icon={IconName.AtSymbol} />}
        {showPrice && (
          <PriceInlineFormatted
            entity={entity}
            price={price}
            endPrice={endPrice}
            security={security}
            showPriceReference={showPriceReference}
            showFees={showFees}
            smartTruncate={smartTruncate}
            priceField={priceField}
          />
        )}
        {showStrategy &&
          (displayStrategy ? (
            <Text color="colorTextImportant" data-testid="order-strategy">
              {displayStrategy}
            </Text>
          ) : (
            <StrategyPart entity={entity} />
          ))}
        {showLastMarket && lastMarket && <MarketPart lastMarket={lastMarket} />}
      </Wrapper>
    );
  }

  if (type === 'trade') {
    return (
      <Wrapper {...props}>
        {showSymbol && <Text color="colorTextImportant">{security?.DisplaySymbol}</Text>}
        {showSide && entity.Side && <Side side={entity.Side}>{getSideLabel(entity.Side)}</Side>}
        {showQty && (
          <InlineFormattedNumber
            highlightAll
            number={quantity}
            increment={
              isCompoundSecurity
                ? quantityIncrement
                : isCcy
                  ? security?.DefaultPriceIncrement
                  : security?.DefaultSizeIncrement
            }
            specification={security?.SizeDisplaySpec}
            currency={quantityCurrency}
          />
        )}
        {showQty && showPrice && price != null && <Icon icon={IconName.AtSymbol} />}
        {showPrice && price != null && (
          <>
            <InlineFormattedNumber
              highlightAll
              number={price}
              increment={security?.DefaultPriceIncrement}
              specification={security?.PriceDisplaySpec}
              currency={isCcy ? entity.Currency : entity.AmountCurrency}
            />
          </>
        )}
        {showFixingDetails && entity.FixingDetails?.Fixing && (
          <Text color="colorTextSubtle">
            (Fixing:{' '}
            <InlineFormattedNumber
              number={entity.FixingDetails.Fixing}
              currency={security?.QuoteCurrency}
              specification={security?.PriceDisplaySpec}
              increment={security?.DefaultPriceIncrement}
            />
            )
          </Text>
        )}
      </Wrapper>
    );
  }

  const tradedSide: string = entity.TradedSide || '-';

  // Quotes
  // Security can be undefined for Compound instruments (maker side), and Security.BaseCurrency can be undefined for
  // Compound instruments (taker side), so we add in fallbacks on Quote.Currency/AmountCurrency
  return (
    <Wrapper>
      {showSymbol && entity.Symbol}
      {showSide && <QuoteSide side={entity.TradedSide} />}

      {formatMessage(messages.quoteSummary, {
        base: () =>
          isCcy ? (
            <Text color="colorTextImportant">{security.BaseCurrency ?? entity.AmountCurrency}</Text>
          ) : (
            <InlineFormattedNumber
              highlightAll
              number={quantity}
              increment={quantityIncrement}
              specification={security?.SizeDisplaySpec}
              currency={quantityCurrency ?? security?.BaseCurrency ?? entity.Currency}
            />
          ),
        Side: tradedSide,
        quote: () =>
          isCcy ? (
            <InlineFormattedNumber
              highlightAll
              number={quantity}
              increment={security?.DefaultPriceIncrement}
              specification={security?.PriceDisplaySpec}
              currency={security?.QuoteCurrency ?? entity.Currency}
            />
          ) : (
            <Text color="colorTextImportant">{security?.QuoteCurrency ?? entity.AmountCurrency}</Text>
          ),
      })}
      {showLastMarket && lastMarket && <MarketPart lastMarket={lastMarket} />}
    </Wrapper>
  );
}

/**
 * Render a price, if `showPriceReference=true` and a PricingReference is set, that will be rendered instead
 * with the price available in a tooltip.
 */
export function PriceInlineFormatted({
  price,
  endPrice,
  showPriceReference,
  security,
  entity,
  showFees = false,
  smartTruncate = SmartTruncateEnum.None,
  priceField = 'Price',
}: {
  entity: Partial<Pick<ExecutionReport, 'Currency' | 'AmountCurrency' | 'PricingReference' | 'PricingMode'>>;
  showPriceReference: boolean;
  price: string | undefined;
  endPrice?: string | undefined;
  security: Security | undefined;
  showFees?: boolean;
  smartTruncate?: SmartTruncateEnum;
  priceField?: 'Price' | 'AvgPx' | 'LastPx' | 'TradedPx';
}) {
  const placedAmount = getPricingReference(entity);

  // When PricingMode is BPS (e.g., Delta1Spread multileg orders), both the order price
  // and execution prices (AvgPx, etc.) are returned in BPS from the backend
  let priceCurrency = entity.PricingMode === PricingModeEnum.BPS ? PricingModeEnum.BPS : security?.QuoteCurrency;
  if (!priceCurrency && security == null) {
    // Some orders do not have a Symbol (Security). This is the case for compound customer orders. In this case, we use the AmountCurrency instead.
    // We do not yet support counter currency compound customer orders (in which case we would do .Currency)
    priceCurrency = entity.AmountCurrency;
  }

  const EntityPrice = useMemo(() => {
    if (price && endPrice) {
      return (
        <>
          <InlineFormattedNumber
            highlightAll
            number={price}
            increment={security?.DefaultPriceIncrement}
            specification={security?.PriceDisplaySpec}
            smartTruncate={smartTruncate}
          />
          {' - '}
          <InlineFormattedNumber
            highlightAll
            number={endPrice}
            increment={security?.DefaultPriceIncrement}
            specification={security?.PriceDisplaySpec}
            currency={priceCurrency}
            smartTruncate={smartTruncate}
          />
        </>
      );
    }
    if (price) {
      return (
        <InlineFormattedNumber
          highlightAll
          number={price}
          increment={security?.DefaultPriceIncrement}
          specification={security?.PriceDisplaySpec}
          currency={priceCurrency}
          startIcon={showFees ? IconName.Fees : undefined}
          smartTruncate={smartTruncate}
        />
      );
    }
    return null;
  }, [
    price,
    priceCurrency,
    endPrice,
    security?.DefaultPriceIncrement,
    security?.PriceDisplaySpec,
    showFees,
    smartTruncate,
  ]);

  return (
    <>
      {showPriceReference && placedAmount ? (
        <Tooltip
          usePortal
          showUnderline
          disabled={EntityPrice === null}
          tooltip={<Text color="colorTextDefault">Executing Price: {EntityPrice}</Text>}
          targetStyle={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <Box>
            {placedAmount.currency === '%' ? 'IV ' : null}
            <InlineFormattedNumber
              number={placedAmount.value}
              currency={placedAmount.currency}
              smartTruncate={smartTruncate}
            />
          </Box>
        </Tooltip>
      ) : (
        EntityPrice
      )}
    </>
  );
}
